import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_RECOVERY_ATTEMPTS = 2;

interface UseAudioPlaybackOptions {
  audioUrl?: string;
  refetchAudioUrl?: () => void;
}

const normalizeAudioUrl = (audioUrl?: string) => (audioUrl ? new URL(audioUrl, window.location.href).href : undefined);

// Owns the <audio> element and playback state so the player and transcript stay in sync.
export const useAudioPlayback = ({ audioUrl, refetchAudioUrl }: UseAudioPlaybackOptions = {}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [loadedMetadata, setLoadedMetadata] = useState<{ sourceUrl: string; duration: number } | null>(null);
  const [seekedTo, setSeekedTo] = useState<{ seconds: number } | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackError, setPlaybackError] = useState<string | null>(null);

  const lastTimeRef = useRef(0);
  const resumeRef = useRef<{ seconds: number; wasPlaying: boolean } | null>(null);
  const recoveryAttemptsRef = useRef(0);
  // Playback intent, unlike `isPlaying`, survives the pause a failed load triggers.
  const wantsPlaybackRef = useRef(false);

  // Metadata events can beat the passive source-reset effect when the browser has
  // the audio cached. Associate duration with the source that produced it instead
  // of allowing that later effect to erase valid metadata.
  const duration =
    loadedMetadata && loadedMetadata.sourceUrl === normalizeAudioUrl(audioUrl) ? loadedMetadata.duration : 0;

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      wantsPlaybackRef.current = true;
      try {
        await audio.play();
        setPlaybackError(null);
      } catch {
        wantsPlaybackRef.current = false;
        setPlaybackError('Audio could not be played. Please try refreshing the page.');
      }
    } else {
      wantsPlaybackRef.current = false;
      audio.pause();
    }
  };

  const pause = useCallback(() => {
    wantsPlaybackRef.current = false;
    audioRef.current?.pause();
  }, []);

  const seekTo = useCallback((seconds: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = seconds;
    setCurrentTime(seconds);
  }, []);

  const skipBy = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const target = audio.currentTime + seconds;
    const max = Number.isFinite(audio.duration) ? audio.duration : target;
    seekTo(Math.min(Math.max(target, 0), max));
  };

  const handleLoadedMetadata = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = event.currentTarget;
    setLoadedMetadata({ sourceUrl: audio.currentSrc || normalizeAudioUrl(audioUrl) || '', duration: audio.duration });

    const resume = resumeRef.current;
    if (!resume) return;
    resumeRef.current = null;

    audio.currentTime = resume.seconds;
    setCurrentTime(resume.seconds);

    if (resume.wasPlaying) {
      void audio.play().catch(() => {
        setPlaybackError('Audio could not be played. Please try refreshing the page.');
      });
    }
  };

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = event.currentTarget;
    if (audio.seeking) return;

    lastTimeRef.current = audio.currentTime;

    // Progress means we recovered, so a later expiry gets a fresh set of attempts.
    if (recoveryAttemptsRef.current > 0 && !resumeRef.current) recoveryAttemptsRef.current = 0;
    setCurrentTime(audio.currentTime);
  };

  const handleSeeking = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    lastTimeRef.current = event.currentTarget.currentTime;
  };

  const handleSeeked = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = event.currentTarget;
    lastTimeRef.current = audio.currentTime;
    setCurrentTime(audio.currentTime);
    setSeekedTo({ seconds: audio.currentTime });
  };

  // Refetch presigned url on failure (probably expired)
  const handleError = () => {
    if (!refetchAudioUrl || recoveryAttemptsRef.current >= MAX_RECOVERY_ATTEMPTS) {
      setPlaybackError('Audio could not be loaded. Please try refreshing the page.');
      return;
    }

    recoveryAttemptsRef.current += 1;
    resumeRef.current = { seconds: lastTimeRef.current, wasPlaying: wantsPlaybackRef.current };
    refetchAudioUrl();
  };

  // Reload once the replacement URL has reached the element, so recovery doesn't rely
  // on the browser's implicit reload when the src attribute changes.
  useEffect(() => {
    if (!audioUrl || !resumeRef.current) return;
    audioRef.current?.load();
  }, [audioUrl]);

  // A new source invalidates the previous file's metadata, so callers gating on
  // `duration` don't act on a stale value. Recovery reloads keep their position.
  useEffect(() => {
    if (resumeRef.current) return;
    setCurrentTime(0);
    setSeekedTo(null);
    lastTimeRef.current = 0;
  }, [audioUrl]);

  const audioProps = {
    ref: audioRef,
    src: audioUrl,
    onLoadedMetadata: handleLoadedMetadata,
    onTimeUpdate: handleTimeUpdate,
    onSeeking: handleSeeking,
    onSeeked: handleSeeked,
    onPlay: () => setIsPlaying(true),
    onPause: () => setIsPlaying(false),
    onEnded: () => {
      wantsPlaybackRef.current = false;
      setIsPlaying(false);
    },
    onError: handleError,
  } as const;

  return {
    audioRef,
    audioProps,
    currentTime,
    setCurrentTime,
    duration,
    isPlaying,
    seekedTo,
    playbackError,
    togglePlayback,
    pause,
    seekTo,
    skipBy,
  };
};

export type AudioPlayback = ReturnType<typeof useAudioPlayback>;
